import { z } from "zod";
import type {
  AccountRole,
  BusinessRole,
  EndUserRole,
  SystemRole,
} from "./auth.schema";

export const uuidSchema = z.string().uuid();
export const timestampsSchema = z.object({
  created_at: z.string(),
  updated_at: z.string(),
});

export const AddressSchema = z.object({
  street: z.string().nullable().optional().describe("Street"),
  city: z.string().nullable().optional().describe("City"),
  state: z.string().nullable().optional().describe("State"),
  zip: z.string().nullable().optional().describe("ZIP Code"),
  country: z.string().optional().describe("Country"),
  latitude: z.number().nullable().optional().describe("Latitude"),
  longitude: z.number().nullable().optional().describe("Longitude"),
});
export type Address = z.infer<typeof AddressSchema>;

export interface TenantContext {
  businessId: string;
  accountId: string;
  /** Supabase auth user ID (JWT `sub` claim). Undefined in dev fallback. */
  userId?: string;
  /**
   * core.api_keys.id when the request was authenticated by an API key.
   * ADV-918: surfaced on the context so the async import worker can attribute
   * audit events to the key when reconstructing ctx from an import_jobs row.
   * `resolveActor` reads this as the implicit fallback when no explicit
   * `opts.apiKeyId` is provided.
   */
  apiKeyId?: string;
  /** Legacy account-level role from app_metadata (e.g. "owner", "admin"). Removed in ADV-879 Phase 5. */
  accountRole?: AccountRole;
  /** Legacy business-level role from app_metadata (e.g. "admin", "supervisor"). Removed in ADV-879 Phase 5. */
  businessRole?: BusinessRole;
  /** Unified system role (ADV-879). Mapped from accountRole in Phase 1, read directly from JWT after Phase 4. */
  systemRole?: SystemRole;
  /** End-user role within active business (ADV-879). Only meaningful when systemRole === "user". */
  endUserRole?: EndUserRole | null;
  /** True when a super_admin is viewing another business via x-impersonate-business-id. */
  isImpersonating?: boolean;
  /** The super_admin's own business ID before impersonation override. */
  realBusinessId?: string;
  /**
   * Per-request correlation UUID (ADV-921). Generated once by the
   * `correlationMiddleware` and read by `emitActivityEvent` / `createOperatorNote`
   * so cascading mutations within a single HTTP request share a
   * `metadata.correlation_id` for grouping in the activity feed.
   */
  correlationId?: string;
  /**
   * ADV-1129: when true, the comm-attachment import handler skips the
   * synchronous `processMediaUrl` (fetch + EXIF + R2 PUT) path and instead
   * INSERTs `ops.media_items` rows at `processing_status='pending'`. The
   * MediaImportWorkflow then fans out per-attachment work onto
   * MEDIA_DOWNLOAD_QUEUE and waits durably for the Finalize-stage
   * `media-batch-complete` event. The default (`undefined` / `false`)
   * preserves today's sync behavior for local-dev + script callers.
   */
  useAsyncMediaPipeline?: boolean;
  /**
   * ADV-1129: out-param the comm-attachment handler pushes newly-created
   * pending `media_item_id`s onto when `useAsyncMediaPipeline` is set. The
   * import chunk runner (`importPreparedChunk`) installs a fresh array per
   * chunk, then folds the collected ids onto `ImportChunkTotals.
   * pendingMediaItemIds` so the workflow's `fan-out-media` step can publish
   * exactly the rows this import created. Handler registry's
   * `create(ctx, row)` signature is fixed, so a ctx-borne collector is the
   * narrowest seam. Underscore-prefixed to mark it internal plumbing, never
   * part of the request-auth contract.
   */
  _asyncMediaCarrier?: string[];
  /**
   * ADV-1162: the import job id, threaded onto the context when an import
   * builds it so the comm-attachment async insert can persist
   * `ops.media_items.import_job_id` for provenance + the failure-runbook's
   * per-import triage query. Undefined for non-import callers (operator/app
   * uploads), which leave the column null.
   */
  importJobId?: string;
  /**
   * ADV-1387: the import job's `source` tag, threaded onto the context so
   * entity handlers that resolve nested-child FK `*_external_id` references
   * (e.g. proposal `line_items[].product_external_id`,
   * job `job_items[].product_external_id`) can call `batchResolveExternalIds`
   * with the same `source` the top-level Phase-3 FK pass uses. Like every
   * `external_id` cross-reference, `source` is provenance metadata — it is NOT
   * used as a query predicate (ADV-836) — so a missing value still resolves;
   * the field exists for parity/observability. Undefined for non-import
   * callers, which never invoke the nested-item path.
   */
  importSource?: string;
}

export const GeoPointSchema = z.object({
  latitude: z.number().describe("Latitude"),
  longitude: z.number().describe("Longitude"),
});
export type GeoPoint = z.infer<typeof GeoPointSchema>;

/** Strip PostgREST filter metacharacters from search input to prevent filter injection */
export function sanitizeSearch(s: string): string {
  return s.replace(/[,()]/g, "");
}

/** A `limit` query param with CEILING semantics: any integer ≥ 1 is accepted
 * and clamped to `max` — a limit is a cap, not a demand, so an over-cap value
 * returns `max` rows instead of a 400. Pass `defaultValue` for a defaulted
 * param; omit it (and chain `.optional()` at the call site) for an optional one. */
export function clampedLimit(max: number, defaultValue?: number) {
  const s = z.coerce.number().int().min(1);
  const withDefault = defaultValue != null ? s.default(defaultValue) : s;
  return withDefault.transform((n) => Math.min(n, max));
}

export const PaginationQuerySchema = z.object({
  limit: clampedLimit(100, 50),
  offset: z.coerce.number().min(0).default(0),
});

/** Parse a comma-separated string of UUIDs into `string[] | undefined`.
 * Returns `undefined` when the value is absent or an empty string (no filter).
 * Rejects any member that is not a valid UUID. */
export const csvUuids = () =>
  z
    .string()
    .optional()
    .transform((v) =>
      v == null || v.trim() === ""
        ? undefined
        : v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
    )
    .pipe(z.array(z.string().uuid()).nonempty().optional())
    .describe("Comma-Separated UUID List");

export const SortQuerySchema = z.object({
  sort_by: z.string().optional().describe("Sort By"),
  // schema-check-ignore: query-filter — pagination/sort, no coercion needed
  sort_order: z.enum(["asc", "desc"]).optional().describe("Sort Order"),
});
